import {
  buildStoreSiteContext,
  getLocalYMD,
  isStoreOrderClosedStatus,
  resolveOrderLogisticsDate,
  resolveProductionOperationalDate,
  resolveStoreOrderSiteId,
} from './storeLogistics';
import { sanitizeProductDisplayName } from './productLabels';
import {
  isDeliverableSaleItem,
  isOperationalInventorySaleItem,
} from './saleItems';

const OPEN_LOGISTICS_ORDER_STATUSES = ['recibido', 'en_preparacion', 'listo', 'en_camino'];

const mergeRowsById = (...collections) => {
  const byId = new Map();

  collections.flat().forEach((row) => {
    if (!row?.id) return;
    byId.set(row.id, row);
  });

  return Array.from(byId.values());
};

const normalizeUuidArray = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);

const getLinkedRequestLineIds = (row) => {
  if (!row || typeof row !== 'object') return [];

  const directIds = normalizeUuidArray(row.request_line_ids || row.production_request_line_ids);
  if (directIds.length > 0) return directIds;

  const metadataIds = normalizeUuidArray(
    row.metadata?.request_line_ids || row.metadata?.production_request_line_ids,
  );
  if (metadataIds.length > 0) return metadataIds;

  return [];
};

export const getLogisticsMonthRange = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const start = new Date(year, month, 1, 0, 0, 0, 0);
  const end = new Date(year, month + 1, 0, 23, 59, 59, 999);

  return {
    startISO: start.toISOString(),
    endISO: end.toISOString(),
    startYMD: getLocalYMD(start),
    endYMD: getLocalYMD(end),
  };
};

export const buildSaleItemsBySaleId = (saleItems, { operationalOnly = false } = {}) => {
  const map = new Map();

  (saleItems || []).forEach((item) => {
    const normalizedItem = {
      ...item,
      product_name_snapshot: sanitizeProductDisplayName(
        item.product_name_snapshot || item.products?.name || 'Producto',
      ),
    };

    if (!isDeliverableSaleItem(normalizedItem)) return;
    if (operationalOnly && !isOperationalInventorySaleItem(normalizedItem)) return;

    const rows = map.get(item.sale_id) || [];
    rows.push(normalizedItem);
    map.set(item.sale_id, rows);
  });

  return map;
};

export const buildProductMap = (products) => {
  const map = new Map();
  (products || []).forEach((product) => map.set(product.id, product));
  return map;
};

export const mergeProductsWithStockLevels = (products, stockLevels) => {
  const levelsByProductId = new Map();

  (stockLevels || []).forEach((level) => {
    const productId = level?.product_id;
    if (!productId) return;

    const rows = levelsByProductId.get(productId) || [];
    rows.push({
      site_id: level.site_id || null,
      quantity: Number(level.quantity || 0),
    });
    levelsByProductId.set(productId, rows);
  });

  return (products || []).map((product) => ({
    ...product,
    stock_levels:
      levelsByProductId.get(product.id) ||
      (Array.isArray(product?.stock_levels) ? product.stock_levels : []),
  }));
};

export const getProductStockForSite = (product, selectedSiteId) => {
  const levels = Array.isArray(product?.stock_levels) ? product.stock_levels : [];
  if (selectedSiteId) {
    return levels.reduce((acc, level) => {
      if (String(level?.site_id || '') !== String(selectedSiteId)) return acc;
      return acc + Number(level?.quantity || 0);
    }, 0);
  }
  return levels.reduce((acc, level) => acc + Number(level.quantity || 0), 0);
};

const normalizeProductLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const isPlaceholderProductLabel = (value) =>
  ['producto', 'product', 'sin nombre', 'nuevo producto'].includes(normalizeProductLabel(value));

export const buildDailyLogisticsSummary = ({ orders, productions, itemMapBySaleId, productionRequests = [] }) => {
  const summary = {};
  const requestLineDateById = new Map(
    (productionRequests || [])
      .filter((line) => line?.id)
      .map((line) => [line.id, String(line.requested_date || '').trim()]),
  );

  (orders || []).forEach((order) => {
    const day = Number(resolveOrderLogisticsDate(order).slice(8, 10));
    const items = itemMapBySaleId.get(order.sale_id) || [];
    const info = summary[day] || {
      ordersCount: 0,
      pendingUnits: 0,
      productionUnits: 0,
      products: new Set(),
    };

    info.ordersCount += 1;
    items.forEach((item) => {
      info.pendingUnits += Number(item.quantity || 0);
      if (item.product_id) info.products.add(item.product_id);
    });
    summary[day] = info;
  });

  (productions || []).forEach((row) => {
    const linkedDates = Array.from(
      new Set(
        getLinkedRequestLineIds(row)
          .map((lineId) => requestLineDateById.get(lineId))
          .filter(Boolean),
      ),
    );
    const productionDates = linkedDates.length > 0 ? linkedDates : [resolveProductionOperationalDate(row)];

    productionDates.forEach((dateKey) => {
      const day = Number(String(dateKey || '').slice(8, 10));
      if (!day) return;

      const info = summary[day] || {
        ordersCount: 0,
        pendingUnits: 0,
        productionUnits: 0,
        products: new Set(),
      };

      info.productionUnits += Number(row.cantidad || 0);
      if (row.producto_id) info.products.add(row.producto_id);
      summary[day] = info;
    });
  });

  Object.keys(summary).forEach((key) => {
    summary[key] = {
      ordersCount: summary[key].ordersCount,
      pendingUnits: summary[key].pendingUnits,
      productionUnits: summary[key].productionUnits,
      productsCount: summary[key].products.size,
    };
  });

  return summary;
};

export const buildOperationalRowsForDate = ({
  selectedDateKey,
  orders,
  productionRequests,
  productions = [],
  itemMapBySaleId,
  productMap,
  selectedSiteId,
  carryPreviousBalance = false,
  showAllProducts = false,
}) => {
  const pendingByProduct = new Map();
  const sentByProduct = new Map();
  const completedByProduct = new Map();
  const keys = new Set();
  const requestLineById = new Map(
    (productionRequests || [])
      .filter((line) => line?.id)
      .map((line) => [line.id, line]),
  );

const ensureMapRow = (targetMap, key, productId, name) => {
  if (!targetMap.has(key)) {
    targetMap.set(key, {
      productId: productId || null,
      name: sanitizeProductDisplayName(name || 'Producto'),
      byDate: new Map(),
    });
  }

  const row = targetMap.get(key);
  if (!row.productId && productId) row.productId = productId;
  if (!row.name && name) row.name = sanitizeProductDisplayName(name);
  keys.add(key);
  return row;
};

  (orders || []).forEach((order) => {
    const orderDate = resolveOrderLogisticsDate(order);

    (itemMapBySaleId.get(order.sale_id) || []).forEach((item) => {
      if (!item.product_id) return;

      const key = item.product_id || item.product_name_snapshot;
      if (!key) return;

      if (!pendingByProduct.has(key)) {
        pendingByProduct.set(key, {
          productId: item.product_id || null,
          name: sanitizeProductDisplayName(item.product_name_snapshot || 'Producto'),
          byDate: new Map(),
        });
      }

      const row = pendingByProduct.get(key);
      const quantity = Number(item.quantity || 0);
      row.byDate.set(orderDate, Number(row.byDate.get(orderDate) || 0) + quantity);
      keys.add(key);
    });
  });

  (productionRequests || []).forEach((row) => {
    const requestDate = String(row.requested_date || '').trim();
    const key = row.product_id || row.product_name;
    if (!key) return;

    if (!sentByProduct.has(key)) {
      sentByProduct.set(key, {
        productId: row.product_id || null,
        name: sanitizeProductDisplayName(row.product_name || 'Producto'),
        byDate: new Map(),
      });
    }

    const item = sentByProduct.get(key);
    const quantity = Number(row.quantity || 0);
    item.byDate.set(requestDate, Number(item.byDate.get(requestDate) || 0) + quantity);
    keys.add(key);
  });

  (productions || []).forEach((row) => {
    const linkedLines = getLinkedRequestLineIds(row)
      .map((lineId) => requestLineById.get(lineId))
      .filter(Boolean);

    if (linkedLines.length > 0) {
      const consumedTargets = new Set();

      linkedLines.forEach((line) => {
        const key = line.product_id || line.product_name || row.producto_id || row.producto_nombre || row.id;
        const dateKey = String(line.requested_date || '').trim() || resolveProductionOperationalDate(row);
        const consumptionKey = `${dateKey}::${key}`;

        if (!key || consumedTargets.has(consumptionKey)) return;
        consumedTargets.add(consumptionKey);

        const completedRow = ensureMapRow(
          completedByProduct,
          key,
          line.product_id || row.producto_id || null,
          sanitizeProductDisplayName(line.product_name || row.producto_nombre || 'Producto'),
        );

        completedRow.byDate.set(
          dateKey,
          Number(completedRow.byDate.get(dateKey) || 0) + Number(row.cantidad || 0),
        );
      });

      return;
    }

    const candidateSiteIds = new Set(
      [row.sitio_id, row.site_id, row.source_site_id, row.target_site_id]
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    );

    if (selectedSiteId && candidateSiteIds.size > 0 && !candidateSiteIds.has(String(selectedSiteId))) {
      return;
    }

    const key = row.producto_id || row.producto_nombre || row.id;
    if (!key) return;

    const dateKey = resolveProductionOperationalDate(row);
    const completedRow = ensureMapRow(
      completedByProduct,
      key,
      row.producto_id || null,
      sanitizeProductDisplayName(row.producto_nombre || 'Producto'),
    );

    completedRow.byDate.set(
      dateKey,
      Number(completedRow.byDate.get(dateKey) || 0) + Number(row.cantidad || 0),
    );
  });

  if (showAllProducts) {
    productMap.forEach((product) => {
      if (!product?.id) return;
      keys.add(product.id);
    });
  }

  return Array.from(keys)
    .map((key) => {
      const pending = pendingByProduct.get(key);
      const sent = sentByProduct.get(key);
      const completed = completedByProduct.get(key);
      const productId = pending?.productId || sent?.productId || completed?.productId || null;
      const product = productMap.get(productId);
      const resolvedName = sanitizeProductDisplayName(
        pending?.name || sent?.name || completed?.name || product?.name || 'Producto',
      );
      const rawStock = getProductStockForSite(product, selectedSiteId);
      const currentStock = Math.max(0, rawStock);
      const sentToProduction = Number(sent?.byDate?.get(selectedDateKey) || 0);
      const completedProduction = Number(completed?.byDate?.get(selectedDateKey) || 0);
      const productionPending = Math.max(0, sentToProduction - completedProduction);
      const pendingOrders = Number(pending?.byDate?.get(selectedDateKey) || 0);

      if (!showAllProducts && sentToProduction <= 0 && completedProduction <= 0) {
        return null;
      }

      const hasVisibleActivity =
        currentStock !== 0 ||
        sentToProduction !== 0 ||
        completedProduction !== 0 ||
        productionPending !== 0 ||
        pendingOrders !== 0;

      if (showAllProducts && !hasVisibleActivity && isPlaceholderProductLabel(resolvedName)) {
        return null;
      }

        return {
          key,
          name: resolvedName,
          rawStock,
          stock: currentStock,
          sentToProduction,
          completedProduction,
        productionPending,
        pendingOrders,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.pendingOrders !== a.pendingOrders) return b.pendingOrders - a.pendingOrders;
      if (b.sentToProduction !== a.sentToProduction) return b.sentToProduction - a.sentToProduction;
      return a.name.localeCompare(b.name, 'es');
    });
};

export const fetchLogisticsMonthDataset = async ({
  supabase,
  organizationId,
  range,
  selectedSiteId,
  filterProductionsBySite = true,
}) => {
  const [ordersRes, storeConfigsRes, organizationRes] = await Promise.all([
    supabase
      .from('store_orders')
      .select(`
        id, sale_id, status, created_at, shipping_address, customer_snapshot, tracking_code, delivery_type, store_slug,
        sale:sales!store_orders_sale_id_fkey ( id, site_id, total )
      `)
      .eq('organization_id', organizationId)
      .in('status', OPEN_LOGISTICS_ORDER_STATUSES)
      .order('created_at', { ascending: false }),
    supabase
      .from('store_config')
      .select('slug, default_fulfillment_site_id')
      .eq('organization_id', organizationId),
    supabase
      .from('organizations')
      .select('default_site_id')
      .eq('id', organizationId)
      .maybeSingle(),
  ]);

  if (ordersRes.error) throw ordersRes.error;
  if (storeConfigsRes.error) throw storeConfigsRes.error;
  if (organizationRes.error) throw organizationRes.error;

  const storeSiteContext = buildStoreSiteContext({
    storeConfigs: storeConfigsRes.data || [],
    organizationDefaultSiteId: organizationRes.data?.default_site_id || null,
  });

  const orders = (ordersRes.data || [])
    .map((order) => ({
      ...order,
      sale: Array.isArray(order.sale) ? order.sale[0] || null : order.sale || null,
      logistics_site_id: resolveStoreOrderSiteId(order, storeSiteContext),
    }))
    .filter((order) => !isStoreOrderClosedStatus(order.status))
    .filter((order) => !selectedSiteId || order.logistics_site_id === selectedSiteId)
    .filter((order) => {
      const date = resolveOrderLogisticsDate(order);
      return date >= range.startYMD && date <= range.endYMD;
    });

  const saleIds = Array.from(new Set(orders.map((order) => order.sale_id).filter(Boolean)));
  const itemsRes = saleIds.length
    ? await supabase
        .from('sale_items')
        .select('sale_id, product_id, quantity, product_name_snapshot')
        .in('sale_id', saleIds)
    : { data: [], error: null };

  if (itemsRes.error) throw itemsRes.error;

  const productionRes = await fetchOperationalProductions({
    supabase,
    organizationId,
    selectedSiteId: filterProductionsBySite ? selectedSiteId : '',
    range,
    includeSiteName: true,
  });
  if (productionRes.error) throw productionRes.error;

  const productIds = Array.from(
    new Set([
      ...(itemsRes.data || []).map((item) => item.product_id).filter(Boolean),
      ...(productionRes.data || []).map((row) => row.producto_id).filter(Boolean),
    ]),
  );

  const productsRes = productIds.length
    ? await supabase
        .from('products')
        .select('id, name, stock_levels(quantity, site_id)')
        .eq('organization_id', organizationId)
        .in('id', productIds)
    : { data: [], error: null };

  if (productsRes.error) throw productsRes.error;

  return {
    orders,
    saleItems: itemsRes.data || [],
    productions: productionRes.data || [],
    products: productsRes.data || [],
  };
};

export const fetchOperationalProductions = async ({
  supabase,
  organizationId,
  selectedSiteId,
  range,
  includeSiteName = false,
}) => {
  const select = includeSiteName
    ? 'id, producto_id, producto_nombre, cantidad, fecha, sitio_id, source_site_id, operational_date, production_request_line_ids, activo, sitios:sitio_id ( nombre )'
    : 'id, producto_id, producto_nombre, cantidad, fecha, sitio_id, source_site_id, operational_date, production_request_line_ids, activo';

  let byOperationalDateQuery = supabase
    .from('produccion')
    .select(select)
    .eq('organization_id', organizationId)
    .eq('activo', true)
    .gte('operational_date', range.startYMD)
    .lte('operational_date', range.endYMD)
    .order('operational_date', { ascending: true })
    .order('fecha', { ascending: true });

  let legacyByActualDateQuery = supabase
    .from('produccion')
    .select(select)
    .eq('organization_id', organizationId)
    .eq('activo', true)
    .is('operational_date', null)
    .gte('fecha', range.startISO)
    .lte('fecha', range.endISO)
    .order('fecha', { ascending: true });

  if (selectedSiteId) {
    byOperationalDateQuery = byOperationalDateQuery.eq('sitio_id', selectedSiteId);
    legacyByActualDateQuery = legacyByActualDateQuery.eq('sitio_id', selectedSiteId);
  }

  const [byOperationalDateRes, legacyByActualDateRes] = await Promise.all([
    byOperationalDateQuery,
    legacyByActualDateQuery,
  ]);

  if (byOperationalDateRes.error) return byOperationalDateRes;
  if (legacyByActualDateRes.error) return legacyByActualDateRes;

  const data = mergeRowsById(byOperationalDateRes.data || [], legacyByActualDateRes.data || []).sort((left, right) => {
    const leftDate = resolveProductionOperationalDate(left);
    const rightDate = resolveProductionOperationalDate(right);
    if (leftDate !== rightDate) return leftDate.localeCompare(rightDate);
    return new Date(left.fecha).getTime() - new Date(right.fecha).getTime();
  });

  return { data, error: null };
};
